import { graphlib } from "@dagrejs/dagre";
import type { GraphEdge, GraphNode, ParsedRuleGraph, RawRuleGraph, RuleGraph } from "./GraphTypes";

const BUILT_SYMBOL = Symbol("built");

type RawGraphEdge = GraphEdge & { name?: string; value?: any };
type RawRuleGraphWithValue = RawRuleGraph & {
  value?: {
    noEdges?: boolean;
    instancePaths?: Record<string, string>;
    minimal?: boolean;
    debug?: boolean;
  };
  [BUILT_SYMBOL]?: RawRuleGraphWithValue;
};

const isParsedGraph = (graph: any): graph is ParsedRuleGraph => typeof graph?.node === "function";

const edgeKey = (edge: RawGraphEdge) => `${edge.v}\u0000${edge.w}\u0000${edge.name ?? ""}`;

const indexRawNodes = (graph: RawRuleGraphWithValue) => {
  const nodes = new Map<string, GraphNode>();
  for (const node of graph.nodes || []) {
    if (node?.value) {
      nodes.set(node.v, node.value);
    }
  }
  return nodes;
};

const indexRawEdges = (graph: RawRuleGraphWithValue) => {
  const edges = new Map<string, RawGraphEdge>();
  for (const edge of (graph.edges || []) as RawGraphEdge[]) {
    edges.set(edgeKey(edge), edge);
  }
  return edges;
};

const setRawNode = (graph: RawRuleGraphWithValue, nodeId: string, value: GraphNode) => {
  const existing = graph.nodes.find((node) => node.v === nodeId);
  if (existing) {
    existing.value = value;
    return;
  }
  graph.nodes.push({ v: nodeId, value });
};

const normalizeRawEdge = (edge: RawGraphEdge): RawGraphEdge =>
  ({
    v: edge.v,
    w: edge.w,
    ...(edge.name !== undefined ? { name: edge.name } : {}),
  }) as RawGraphEdge;

const indexResultRawEdges = (graph: RawRuleGraphWithValue) => {
  const edges = new Map<string, RawGraphEdge>();
  for (const edge of (graph.edges || []) as RawGraphEdge[]) {
    edges.set(edgeKey(edge), edge);
  }
  return edges;
};

const setRawEdge = (graph: RawRuleGraphWithValue, edge: RawGraphEdge, resultEdgesByKey: Map<string, RawGraphEdge>) => {
  const normalized = normalizeRawEdge(edge);
  const key = edgeKey(normalized);
  const existing = resultEdgesByKey.get(key);
  if (existing) {
    delete existing.value;
    existing.name = normalized.name;
    return;
  }
  (graph.edges as RawGraphEdge[]).push(normalized);
  resultEdgesByKey.set(key, normalized);
};

const decompressRawEdges = (
  ruleGraph: RawRuleGraphWithValue,
  minimalGraph: RawRuleGraphWithValue,
  resultGraph: RawRuleGraphWithValue,
) => {
  if (minimalGraph.value?.noEdges === true) {
    return;
  }

  const resolvedInstancePaths = minimalGraph.value?.instancePaths;
  const edges = new Map<string, RawGraphEdge>();
  for (const edge of (ruleGraph.edges || []) as RawGraphEdge[]) {
    edges.set(edgeKey(edge), edge);
  }
  const minimalEdgesByKey = indexRawEdges(minimalGraph);
  for (const edge of (minimalGraph.edges || []) as RawGraphEdge[]) {
    edges.set(edgeKey(edge), edge);
  }

  const resultEdgesByKey = indexResultRawEdges(resultGraph);
  for (const [key, edge] of edges) {
    const label = minimalEdgesByKey.get(key)?.value;
    setRawEdge(resultGraph, edge, resultEdgesByKey);

    if (label?.instances && resolvedInstancePaths) {
      for (const [instancePath, targetInstances] of Object.entries(label.instances) as any) {
        const decompressedInstancePath = resolvedInstancePaths[instancePath];

        for (const targetInstance of targetInstances as string[]) {
          const decompressedTargetPath = resolvedInstancePaths[targetInstance as any];
          const fromNodePath = instancePath === "global" ? edge.v : `${decompressedInstancePath}/${edge.v}`;
          const toNodePath = targetInstance === "global" ? edge.w : `${decompressedTargetPath}/${edge.w}`;
          setRawEdge(resultGraph, { v: fromNodePath, w: toNodePath }, resultEdgesByKey);
        }
      }
    }
  }
};

export const decompressEdges = (ruleGraph: RuleGraph, minimalGraph: RuleGraph, resultGraph: RuleGraph) => {
  if (!isParsedGraph(ruleGraph) && !isParsedGraph(minimalGraph) && !isParsedGraph(resultGraph)) {
    decompressRawEdges(
      ruleGraph as RawRuleGraphWithValue,
      minimalGraph as RawRuleGraphWithValue,
      resultGraph as RawRuleGraphWithValue,
    );
    return;
  }

  ruleGraph = ruleGraph as ParsedRuleGraph;
  minimalGraph = minimalGraph as ParsedRuleGraph;
  resultGraph = resultGraph as ParsedRuleGraph;

  // @ts-ignore
  if (minimalGraph.graph()?.noEdges === true) {
    return;
  }

  // @ts-ignore
  const resolvedInstancePaths = minimalGraph.graph()?.instancePaths;

  // @ts-ignore
  const edges = {...ruleGraph._edgeObjs, ...minimalGraph._edgeObjs};

  for (const edge of Object.values(edges) as any[]) {

    const fromNodeId = edge.v;
    const toNodeId = edge.w;

    const label = minimalGraph.edge(edge);

    resultGraph.setEdge(edge);

    // Check if this is a decision graph edge with instances (from minimiseGraph)

    if (label?.instances && resolvedInstancePaths) {
      //rebuiltGraph.setEdge(edge.v, edge.w);

      // Transform decision graph edges with instances
      for (const [ instancePath, targetInstances ] of Object.entries(label.instances) as any) {
        const decompressedInstancePath = resolvedInstancePaths[instancePath];
        const targetInstanceArray = targetInstances as string[];

        for (const targetInstance of targetInstanceArray) {
          const decompressedTargetPath = resolvedInstancePaths[targetInstance as any] ;
          // Create full node paths by combining instance paths with node IDs
          const fromNodePath = instancePath === "global" ? fromNodeId : `${decompressedInstancePath}/${fromNodeId}`;
          const toNodePath = targetInstance === "global" ? toNodeId : `${decompressedTargetPath}/${toNodeId}`;

          // Only add edge if both nodes exist in our combined graph
          //if (minimalGraph.node(fromNodePath) && minimalGraph.node(toNodePath)) {
          resultGraph.setEdge(fromNodePath, toNodePath);
          //}
        }
      }
    }
  }

}

const buildRawGraph = (
  ruleGraph: RawRuleGraphWithValue,
  decisionGraph: RawRuleGraphWithValue,
): RawRuleGraphWithValue => {
  if (decisionGraph[BUILT_SYMBOL]) {
    return decisionGraph[BUILT_SYMBOL];
  }

  const ruleNodes = indexRawNodes(ruleGraph);
  const graph = decisionGraph[BUILT_SYMBOL] = {
    options: { multigraph: true, directed: true, compound: false },
    value: decisionGraph.value || {},
    nodes: [],
    edges: [],
  } as RawRuleGraphWithValue;

  for (const decisionNode of decisionGraph.nodes || []) {
    const id = decisionNode.v;
    const dNodeValue = decisionNode.value;
    if (!dNodeValue) continue;

    let node: GraphNode | undefined;
    if (id.includes("/")) {
      const info = id.split("/");
      const refId = info.pop();
      if (!refId) continue;

      const baseNode = ruleNodes.get(refId);
      if (!baseNode) continue;
      node = { ...baseNode, hidden: false, ...dNodeValue };
      node.path = id;
      node.index = info.pop();
      node.entity = info.pop();
      node.parent_path = info.length > 0 ? info.join("/") : undefined;

      if (!graph.nodes.some((existing) => existing.v === refId)) {
        setRawNode(graph, refId, { ...baseNode });
      }
    } else {
      const baseNode = ruleNodes.get(id);
      if (!baseNode) continue;
      node = { ...baseNode, ...dNodeValue };
    }

    setRawNode(graph, id, node);
  }

  if (graph.value?.noEdges !== true) {
    decompressRawEdges(ruleGraph, decisionGraph, graph);
  }

  return graph;
};

/**
 * Combines the minimal decision graph with the full graph
 */
export function buildGraph(ruleGraph: ParsedRuleGraph, decisionGraph: ParsedRuleGraph & {
  [BUILT_SYMBOL]?: ParsedRuleGraph;
}): ParsedRuleGraph;
export function buildGraph(ruleGraph: RawRuleGraph, decisionGraph: RawRuleGraph & {
  [BUILT_SYMBOL]?: RawRuleGraphWithValue;
}): RawRuleGraphWithValue;
export function buildGraph(ruleGraph: RuleGraph, decisionGraph: RuleGraph & {
  [BUILT_SYMBOL]?: RuleGraph;
}): RuleGraph {
  if (!isParsedGraph(ruleGraph) && !isParsedGraph(decisionGraph)) {
    return buildRawGraph(ruleGraph as RawRuleGraphWithValue, decisionGraph as RawRuleGraphWithValue);
  }

  ruleGraph = ruleGraph as ParsedRuleGraph;
  decisionGraph = decisionGraph as ParsedRuleGraph & { [BUILT_SYMBOL]?: ParsedRuleGraph };

  if (decisionGraph[BUILT_SYMBOL]) {
    return decisionGraph[BUILT_SYMBOL];
  }

  // Preserve graph metadata including value.noEdges
  // @ts-ignore
  const originalGraphMeta = decisionGraph.graph() || {};
  const graph = decisionGraph[BUILT_SYMBOL] = new graphlib.Graph({
    multigraph: true,
  });
  // Copy graph-level metadata to the new graph
  graph.setGraph(originalGraphMeta);
  for (const id of decisionGraph.nodes()) {
    let node: any;

    const dNodeValue = decisionGraph.node(id);
    if (!dNodeValue) continue;

    if (id.includes("/")) {
      // Part of an entity chain
      const info = id.split("/");
      const refId = info.pop();
      if (!refId) continue;

      const baseNode = ruleGraph.node(refId);
      if (!baseNode) continue;
      node = {...baseNode};
      node.hidden = false;
      Object.assign(node, dNodeValue);

     // if (!node.path) {
        node.path = id;
        node.index = info.pop();
        node.entity = info.pop();
        node.parent_path = info.length > 0 ? info.join("/") : undefined;
      //}

      // Check if the hidden exists - as it still needs to be added
      // so that anything that checks hidden (like the AttributeExplanation) still works
      if (!graph.node(refId) && baseNode) {
        graph.setNode(refId, { ...baseNode});
      }

    } else {
      const baseNode = ruleGraph.node(id);
      if (!baseNode) continue;
      node = {...baseNode};
      if (node) Object.assign(node, dNodeValue);
    }

    if (node) {
      graph.setNode(id, node);
    }
  }

  // Check if noEdges is set in graph.value
  // @ts-ignore
  const noEdges = originalGraphMeta?.noEdges === true;

  // Only add edges if noEdges is not true
  if (!noEdges) {
    decompressEdges(ruleGraph, decisionGraph, graph);

    // Then do the edges
    /*decisionGraph.edges().forEach((edge) => {
      if (graph.node(edge.v) && graph.node(edge.w)) {
        graph.setEdge(edge.v, edge.w);
      }
    });*/
  }

  return graph as ParsedRuleGraph;
}
